import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { HeatmapLayer as DeckHeatmapLayer } from "@deck.gl/aggregation-layers";
import LinearProgress from "@mui/material/LinearProgress";
import { useLocation } from "react-router-dom";
import { ClientApi } from "../../../API/ClientApi";

type HeatmapPoint = {
  lat: number;
  lng: number;
  weight?: number;
};

function AppHeatMapComponent() {
  const location = useLocation();

  const isNewFleetDemandRoute = location.pathname.includes(
    "/NewFleetDemandForm"
  );

  const containerStyle = useMemo(
    () => ({
      width: "100%",
      height: isNewFleetDemandRoute ? "44rem" : "16.5rem",
    }),
    [isNewFleetDemandRoute]
  );

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [heatmapData, setHeatmapData] = useState<HeatmapPoint[]>([]);
  const overlayRef = useRef<GoogleMapsOverlay | null>(null);

  useEffect(() => {
    const fetchHeatmapData = async () => {
      try {
        const data = await ClientApi.api.googleMapsOnDutyRiders();

        const points: HeatmapPoint[] = data
          .filter((e) => e.latitude && e.longitude)
          .map((e) => ({
            lat: parseFloat(e.latitude.toString()),
            lng: parseFloat(e.longitude.toString()),
            weight: 1,
          }));

        setHeatmapData(points);
      } catch (error) {
        console.error("Heatmap data fetch failed:", error);
      }
    };

    fetchHeatmapData();
  }, []);

  useEffect(() => {
    if (!map || heatmapData.length === 0) return;

    const heatmapLayer = new DeckHeatmapLayer<HeatmapPoint>({
      id: "riders-heatmap",
      data: heatmapData,
      getPosition: (d) => [d.lng, d.lat],
      getWeight: (d) => d.weight || 1,

      // similar to old radius/opacity settings
      radiusPixels: 30,
      intensity: 1,
      threshold: 0.05,
      opacity: 0.6,
    });

    if (!overlayRef.current) {
      overlayRef.current = new GoogleMapsOverlay({
        layers: [heatmapLayer],
      });

      overlayRef.current.setMap(map);
    } else {
      overlayRef.current.setProps({
        layers: [heatmapLayer],
      });
    }

    const firstPoint = heatmapData[0];
    map.setCenter({
      lat: firstPoint.lat,
      lng: firstPoint.lng,
    });
  }, [map, heatmapData]);

  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, []);

  const onLoad = (mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  };

  const onUnmount = () => {
    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
    }

    setMap(null);
  };

  return (
    <>
      {heatmapData.length > 0 ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          zoom={8}
          center={{
            lat: heatmapData[0].lat,
            lng: heatmapData[0].lng,
          }}
          options={{
            fullscreenControl: true,
            zoomControl: true,
          }}
          onLoad={onLoad}
          onUnmount={onUnmount}
        />
      ) : (
        <div className="w-full">
          <LinearProgress
            sx={{
              backgroundColor: "white",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#00CD5B",
              },
            }}
          />
        </div>
      )}
    </>
  );
}

export const AppHeatMap = React.memo(AppHeatMapComponent);